import { Injectable } from '@angular/core'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { of } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import { environment } from 'src/environments/environment'

/** Discounts — mirrors stacks-agents `src/app/api/discounts/`. */
@Injectable({ providedIn: 'root' })
export class DiscountsApiService {
  constructor(private http: HttpClient) {}

  /** Builder JWT for the stacks-agents API */
  private agentsAuthHeaders(): HttpHeaders {
    return new HttpHeaders({ Authorization: `Bearer ${localStorage.getItem('token')}` })
  }

  getDiscounts(projectId: string): Promise<any[]> {
    return this.http
      .get<{ data?: any[] }>(`${environment.agentsUrl}v4/retrieval/discounts`, {
        headers: this.agentsAuthHeaders(),
        params: { projectId },
      })
      .pipe(
        map(body => body?.data ?? []),
        catchError(() => of([])),
      )
      .toPromise() as Promise<any[]>
  }

  getDiscount(projectId: string, discountId: string): Promise<any | null> {
    return this.http
      .get<{ data?: any }>(`${environment.agentsUrl}v4/retrieval/discounts/by-id`, {
        headers: this.agentsAuthHeaders(),
        params: { projectId, discountId },
      })
      .pipe(
        map(body => body?.data ?? null),
        catchError(() => of(null)),
      )
      .toPromise() as Promise<any | null>
  }

  /** Creates a discount with its product and category links. @returns The new discount UUID, or null on failure. */
  async insertDiscount(projectId: string, discount: any): Promise<string | null> {
    const res = await this.http
      .post<{ data: { id: string } }>(`${environment.agentsUrl}v4/creation/discounts`,
        { projectId, discount },
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
    return res?.data?.id ?? null
  }

  /** Updates an existing discount and re-syncs its junction tables. */
  async updateDiscount(projectId: string, discountId: string, discount: any): Promise<void> {
    await this.http
      .patch(`${environment.agentsUrl}v4/update/discounts`,
        { projectId, discountId, discount },
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }

  /** Hard-deletes a discount (junction rows cascade). */
  async deleteDiscount(projectId: string, discountId: string): Promise<void> {
    await this.http
      .request('DELETE', `${environment.agentsUrl}v4/deletion/discounts`, {
        headers: this.agentsAuthHeaders(),
        body: { projectId, discountId },
      })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }
}
